import React, { useState, useMemo } from 'react';
import {
  useGetRentsQuery,
  useDeleteRentMutation,
} from '../../slices/rentsApiSlice';
import {
  Table,
  Spinner,
  Alert,
  Button,
  Form,
  Row,
  Col,
  Image,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const RentListScreen = () => {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [landlordFilter, setLandlordFilter] = useState('');
  const [groupBy, setGroupBy] = useState('');
  const [sortField, setSortField] = useState('startDate');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const { data: rents, isLoading, error } = useGetRentsQuery();
  const [deleteRent] = useDeleteRentMutation();

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this rent record?')) {
      try {
        await deleteRent(id).unwrap();
      } catch (err) {
        alert(err?.data?.message || err.error);
      }
    }
  };

  const filtered = useMemo(() => {
    return rents
      ?.filter((r) => {
        const startDate = new Date(r.startDate);
        const endDate = new Date(r.endDate);
        const inRange =
          (!start || new Date(start) <= endDate) &&
          (!end || new Date(end) >= startDate);
        const tenantMatch = tenantFilter
          ? r.tenant?.name?.toLowerCase().includes(tenantFilter.toLowerCase())
          : true;
        const landlordMatch = landlordFilter
          ? r.landlord?.name?.toLowerCase().includes(landlordFilter.toLowerCase())
          : true;
        return inRange && tenantMatch && landlordMatch;
      })
      ?.sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];
        if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
        return aVal < bVal ? 1 : -1;
      });
  }, [rents, start, end, tenantFilter, landlordFilter, sortField, sortOrder]);

  const grouped = useMemo(() => {
    if (!groupBy) return null;
    return filtered.reduce((acc, rent) => {
      const key = rent[groupBy]?._id || 'Unknown';
      if (!acc[key]) {
        acc[key] = {
          group: rent[groupBy],
          records: [],
        };
      }
      acc[key].records.push(rent);
      return acc;
    }, {});
  }, [filtered, groupBy]);

  const paginated = useMemo(() => {
    if (groupBy || !filtered) return null;
    return filtered.slice((page - 1) * pageSize, page * pageSize);
  }, [filtered, groupBy, page]);

  const totals = useMemo(() => {
    const source = groupBy
      ? Object.values(grouped || {}).flatMap((g) => g.records || [])
      : Array.isArray(filtered) ? filtered : [];

    return source.reduce(
      (acc, r) => {
        acc.totalRent += r.totalRent || 0;
        acc.totalMonths += r.numberOfMonths || 0;
        return acc;
      },
      { totalRent: 0, totalMonths: 0 }
    );
  }, [filtered, grouped, groupBy]);

  const handleExportToExcel = () => {
    const exportData = filtered.map((r) => ({
      Tenant: r.tenant?.name || '',
      House: r.house?.name || '',
      Landlord: r.landlord?.name || '',
      'Start Date': new Date(r.startDate).toLocaleDateString(),
      'End Date': new Date(r.endDate).toLocaleDateString(),
      'Last Rent End': r.lastRentEnd ? new Date(r.lastRentEnd).toLocaleDateString() : '',
      'Number of Months': r.numberOfMonths,
      'Total Rent': r.totalRent,
      'Date Paid': r.datePaid ? new Date(r.datePaid).toLocaleDateString() : '',
      'Recorded At': new Date(r.date).toLocaleDateString(),
      Remark: r.remark || '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Rents');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, `rents_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <>
      <h1 className="mb-4">Rent Records</h1>

      <Form className="mb-3">
        <Row className="align-items-end">
          <Col md={4}>
            <Form.Label>Start Date</Form.Label>
            <Form.Control type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            <Button variant="success" className="mt-2" onClick={handleExportToExcel}>
              Export to Excel
            </Button>
            <div className="mt-2">
              <Link to="/admin/rentimportscreen" className="btn btn-light me-2 mt-2">AddViaExcel</Link>
              <Link to="/admin/renteditimportscreen" className="btn btn-light me-2 mt-2">EditViaExcel</Link>
              <Link to="/create/rent" className="btn btn-light me-2 mt-2">Add Rent</Link>
              <Link to="/rentproratedscreen" className="btn btn-light mt-2">Rent ByYear</Link>
              <Link to='/create/form' className='btn btn-light my-3'>Add Data</Link>
            </div>
          </Col>
          <Col md={2}>
            <Form.Label>End Date</Form.Label>
            <Form.Control type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </Col>
          <Col md={2}>
            <Form.Label>Tenant</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search tenant"
              value={tenantFilter}
              onChange={(e) => setTenantFilter(e.target.value)}
            />
          </Col>
          <Col md={2}>
            <Form.Label>Landlord</Form.Label>
            <Form.Control
              type="text"
              placeholder="Search landlord"
              value={landlordFilter}
              onChange={(e) => setLandlordFilter(e.target.value)}
            />
          </Col>
          <Col md={2}>
            <Form.Label>Group By</Form.Label>
            <Form.Select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
              <option value="">None</option>
              <option value="house">House</option>
              <option value="landlord">Landlord</option>
            </Form.Select>
          </Col>
          <Col md={2}>
            <Form.Label>Sort By</Form.Label>
            <Form.Select value={sortField} onChange={(e) => setSortField(e.target.value)}>
              <option value="startDate">Start Date</option>
              <option value="endDate">End Date</option>
              <option value="totalRent">Total Rent</option>
              <option value="date">Recorded At</option>
            </Form.Select>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col md={2}>
            <Form.Check
              type="switch"
              label={`Sort ${sortOrder === 'asc' ? '↑' : '↓'}`}
              checked={sortOrder === 'asc'}
              onChange={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
            />
          </Col>
        </Row>
      </Form>

      {isLoading ? (
        <Spinner animation="border" />
      ) : error ? (
        <Alert variant="danger">{error?.data?.message || error.error}</Alert>
      ) : groupBy ? (
        <>
          {Object.values(grouped).map((group) => {
            const groupTotals = group.records.reduce(
              (acc, r) => {
                acc.totalRent += r.totalRent || 0;
                acc.totalMonths += r.numberOfMonths || 0;
                return acc;
              },
              { totalRent: 0, totalMonths: 0 }
            );

                         return (
              <div key={group.group?._id || 'unknown'} className="mb-4">
                <h5 className="text-primary">
                  Group: {group.group?.name || 'Unknown'}
                </h5>

                <div className="mb-2 fw-bold text-end">
                  <span className="me-4">Total Months: {groupTotals.totalMonths}</span>
                  <span>Total Rent: {groupTotals.totalRent.toLocaleString()}</span>
                </div>

                <Table striped bordered hover responsive className="table-sm">
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Start</th>
                      <th>End</th>
                      <th>Last End</th>
                      <th>Months</th>
                      <th>Total</th>
                      <th>Paid</th>
                      <th>Recorded</th>
                      <th>Images</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.records.map((rent) => (
                      <tr key={rent._id}>
                        <td>{rent.tenant?.name}</td>
                        <td>{new Date(rent.startDate).toLocaleDateString()}</td>
                        <td>{new Date(rent.endDate).toLocaleDateString()}</td>
                        <td>
                          {rent.lastRentEnd
                            ? new Date(rent.lastRentEnd).toLocaleDateString()
                            : '—'}
                        </td>
                        <td>{rent.numberOfMonths}</td>
                        <td>{rent.totalRent?.toLocaleString()}</td>
                        <td>
                          {rent.datePaid
                            ? new Date(rent.datePaid).toLocaleDateString()
                            : '—'}
                        </td>
                        <td>{new Date(rent.date).toLocaleDateString()}</td>
                        <td>
                          {rent.images?.map((img, i) => (
                            <Image
                              key={i}
                              src={img}
                              alt="rent-img"
                              thumbnail
                              style={{ width: '40px', height: '40px', marginRight: '4px' }}
                            />
                          ))}
                        </td>
                        <td>
                          <Link
                            to={`/rent/${rent._id}`}
                            className="btn btn-sm btn-light me-1"
                          >
                            Details
                          </Link>
                          <Link
                            to={`/admin/rent/${rent._id}/edit`}
                            className="btn btn-sm btn-light me-1"
                          >
                            Edit
                          </Link>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDelete(rent._id)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            );
          })}

          {/* Grand Totals */}
          <div className="mt-4 fw-bold text-end">
            <span className="me-4">Grand Total Months: {totals.totalMonths}</span>
            <span>Grand Total Rent: {totals.totalRent.toLocaleString()}</span>
          </div>
        </>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>House</th>
                <th>Landlord</th>
                <th>Start</th>
                <th>End</th>
                <th>Last End</th>
                <th>Months</th>
                <th>Total</th>
                <th>Paid</th>
                <th>Recorded</th>
                <th>Images</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated?.map((rent) => (
                <tr key={rent._id}>
                  <td>{rent.tenant?.name}</td>
                  <td>{rent.house?.name}</td>
                  <td>{rent.landlord?.name}</td>
                  <td>{new Date(rent.startDate).toLocaleDateString()}</td>
                  <td>{new Date(rent.endDate).toLocaleDateString()}</td>
                  <td>
                    {rent.lastRentEnd
                      ? new Date(rent.lastRentEnd).toLocaleDateString()
                      : '—'}
                  </td>
                  <td>{rent.numberOfMonths}</td>
                  <td>{rent.totalRent?.toLocaleString()}</td>
                  <td>
                    {rent.datePaid
                      ? new Date(rent.datePaid).toLocaleDateString()
                      : '—'}
                  </td>
                  <td>{new Date(rent.date).toLocaleDateString()}</td>
                  <td>
                    {rent.images?.map((img, i) => (
                      <Image
                        key={i}
                        src={img}
                        alt="rent-img"
                        thumbnail
                        style={{ width: '40px', height: '40px', marginRight: '4px' }}
                      />
                    ))}
                  </td>
                  <td>
                    <Link
                      to={`/rent/${rent._id}`}
                      className="btn btn-sm btn-light me-1"
                    >
                      Details
                    </Link>
                    <Link
                      to={`/admin/rent/${rent._id}/edit`}
                      className="btn btn-sm btn-light me-1"
                    >
                      Edit
                    </Link>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(rent._id)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {/* Totals Summary */}
          <div className="mt-3 fw-bold text-end">
            <span className="me-4">Total Months: {totals.totalMonths}</span>
            <span>Total Rent: {totals.totalRent.toLocaleString()}</span>
          </div>

          {/* Pagination Controls */}
          <div className="d-flex justify-content-center mt-3">
            {[...Array(Math.ceil(filtered.length / pageSize)).keys()].map((x) => (
              <Button
                key={x + 1}
                variant={x + 1 === page ? 'primary' : 'light'}
                className="mx-1"
                onClick={() => setPage(x + 1)}
              >
                {x + 1}
              </Button>
            ))}
          </div>
        </>
      )}
    </>
  );
};

export default RentListScreen;